/**
 * @fileoverview Command Manager for Undo/Redo Operations
 * 
 * This file implements a robust command manager that handles command execution,
 * undo/redo operations, command merging, and batch operations. It provides
 * the core infrastructure for the application's undo/redo system.
 * 
 * Key Features:
 * - Command execution with validation
 * - Undo/redo stack management
 * - Command merging for smooth interactions
 * - Batch operations and transactions
 * - Event emission for UI updates
 * - Stack size limits and cleanup
 * 
 * @author Claude Code
 * @version 1.0.0
 */

import { ICommand, CompositeCommand } from './ICommand';
import { IEventBus } from '../events/IEventBus';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';
import { wrapWithScenarioAwareness, isScenarioEditingActive } from '../../domains/orgchart/commands/ScenarioAwareCommand';
import { isScenarioRecordable } from '../../domains/orgchart/commands/IScenarioRecordable';

/**
 * Recursively wrap scenario-recordable commands, including those inside CompositeCommands.
 * This ensures that commands nested within CompositeCommands are properly intercepted
 * when scenario editing is active.
 */
function wrapCommandForScenario(command: ICommand): ICommand {
  if (command.executionScope === 'presentation') return command;
  // If it's a CompositeCommand, wrap its inner commands
  if (command instanceof CompositeCommand) {
    const innerCommands = command.getCommands();
    const wrappedInnerCommands = innerCommands.map(wrapCommandForScenario);
    return new CompositeCommand(command.name, wrappedInnerCommands, command.description);
  }

  // If the command is scenario-recordable, wrap it
  if (isScenarioRecordable(command)) {
    return wrapWithScenarioAwareness(command);
  }

  // Otherwise return as-is
  return command;
}

/**
 * Configuration options for CommandManager behavior.
 */
export interface ICommandManagerOptions {
  /** Maximum number of commands to keep in undo stack (default: 100) */
  maxStackSize?: number;
  
  /** Enable automatic command merging (default: true) */
  mergingEnabled?: boolean;
  
  /** Time window for command merging in milliseconds (default: 500) */
  mergingTimeWindow?: number;
}

/**
 * Represents an entry in the command history with metadata.
 */
export interface ICommandHistoryEntry {
  /** The command instance */
  command: ICommand;
  
  /** When the command was executed */
  timestamp: number;
  
  /** Additional metadata about the command */
  metadata?: Record<string, any>;
}

interface CommandQueueEntry {
  command: ICommand;
  resolve: () => void;
  reject: (error: unknown) => void;
}

export type CommandExecutionGuard = (command: ICommand) => boolean | ICommand | Promise<boolean | ICommand>;
export type CommandOperation = 'execute' | 'undo' | 'redo';
export type CommandOperationGuard = (command: ICommand, operation: CommandOperation) => boolean;

/**
 * Manages command execution, undo/redo operations, and command history.
 * 
 * The CommandManager is the central hub for all command operations in the application.
 * It maintains undo/redo stacks, handles command merging for smooth user interactions,
 * and provides batch operation support for complex multi-step operations.
 * 
 * Key Responsibilities:
 * - Execute commands with validation
 * - Maintain undo/redo stacks with size limits
 * - Merge compatible commands for better UX
 * - Handle batch operations and transactions
 * - Emit events for UI state updates
 * - Provide command history introspection
 * 
 * @example
 * ```typescript
 * const commandManager = new CommandManager(eventBus);
 * 
 * // Execute a command
 * await commandManager.execute(new MoveNodeCommand(...));
 * 
 * // Undo the last operation
 * await commandManager.undo();
 * 
 * // Execute multiple commands as one operation
 * await commandManager.executeBatch([cmd1, cmd2, cmd3], 'Bulk Update');
 * ```
 */
export class CommandManager {
  /** Stack of commands that can be undone */
  private undoStack: ICommand[] = [];
  
  /** Stack of commands that can be redone */
  private redoStack: ICommand[] = [];
  
  /** Flag to prevent concurrent command execution */
  private isExecuting = false;
  
  /** Queue of commands waiting to be executed */
  private commandQueue: CommandQueueEntry[] = [];
  
  /** Last executed command for merging purposes */
  private lastExecutedCommand: ICommand | null = null;

  /** Optional host-owned gate that may pause or cancel a command before execution. */
  private executionGuard: CommandExecutionGuard | null = null;
  private readonly operationGuards = new Set<CommandOperationGuard>();

  private requireOperationAllowed(command: ICommand, operation: CommandOperation): void {
    if ([...this.operationGuards].some((guard) => !guard(command, operation))) {
      throw new Error(`Cannot ${operation} "${command.name}" while the document is read-only`);
    }
  }
  
  /** Timestamp of last command execution for merging */
  private lastExecutionTime = 0;
  
  /** Event bus for notifying UI about command state changes */
  private eventBus: IEventBus | null;
  
  /** Configuration options with defaults applied */
  private options: Required<ICommandManagerOptions>;
  
  /**
   * Creates a new CommandManager instance.
   * 
   * @param eventBus - Optional event bus for emitting command events
   * @param options - Configuration options for command behavior
   */
  constructor(eventBus?: IEventBus, options?: ICommandManagerOptions) {
    this.eventBus = eventBus || null;
    this.options = {
      maxStackSize: options?.maxStackSize ?? 100,
      mergingEnabled: options?.mergingEnabled ?? true,
      mergingTimeWindow: options?.mergingTimeWindow ?? 1200
    };
  }
  
  async execute(command: ICommand): Promise<void> {
    console.log(`[CommandManager] Queuing command: ${command.name}`);
    return new Promise<void>((resolve, reject) => {
      this.commandQueue.push({ command, resolve, reject });
      if (this.isExecuting) {
        console.log(`[CommandManager] Already executing, command queued: ${command.name}`);
        return;
      }
      void this.processCommandQueue();
    });
  }
  
  private async processCommandQueue(): Promise<void> {
    if (this.isExecuting || this.commandQueue.length === 0) {
      console.log(`[CommandManager] Skipping queue processing - isExecuting: ${this.isExecuting}, queue length: ${this.commandQueue.length}`);
      return;
    }
    
    console.log(`[CommandManager] Processing queue with ${this.commandQueue.length} commands`);
    this.isExecuting = true;
    
    try {
      while (this.commandQueue.length > 0) {
        const entry = this.commandQueue.shift()!;
        let command = entry.command;
        console.log(`[CommandManager] Executing command: ${command.name}`);
        const isMatrixSubtypeUpdate = command.name === 'Update Edge';
        if (isMatrixSubtypeUpdate) {
          console.info('[MatrixScenarioDebug] command manager received edge update', {
            commandId: command.id,
            scenarioEditingBeforeGuard: isScenarioEditingActive(),
            hasExecutionGuard: this.executionGuard !== null,
          });
        }

        try {
          this.requireOperationAllowed(command, 'execute');
          const guardAllowed = this.executionGuard ? await this.executionGuard(command) : true;
          // An async draft/confirmation guard may outlive a permission or
          // history-mode transition. Recheck immediately before mutation.
          this.requireOperationAllowed(command, 'execute');
          if (isMatrixSubtypeUpdate) {
            console.info('[MatrixScenarioDebug] command guard result', {
              commandId: command.id,
              allowed: guardAllowed,
              scenarioEditingAfterGuard: isScenarioEditingActive(),
            });
          }
          if (!guardAllowed) {
            console.log(`[CommandManager] Command cancelled by execution guard: ${command.name}`);
            entry.resolve();
            continue;
          }
          if (typeof guardAllowed !== 'boolean') {
            command = guardAllowed;
            this.requireOperationAllowed(command, 'execute');
          }

          // Wrap scenario-recordable commands when in scenario editing mode
          // This also handles CompositeCommands by wrapping their inner commands
          if (isScenarioEditingActive()) {
            const wasComposite = command instanceof CompositeCommand;
            const wasRecordable = isScenarioRecordable(command);
            command = wrapCommandForScenario(command);
            if (wasComposite || wasRecordable) {
              console.log(`[CommandManager] Wrapped command for scenario editing: ${command.name}`);
            }
          } else if (isMatrixSubtypeUpdate) {
            console.info('[MatrixScenarioDebug] edge update is executing outside scenario mode', {
              commandId: command.id,
            });
          }

          if (command.canExecute && !command.canExecute()) {
            console.warn(`[CommandManager] Command "${command.name}" cannot be executed in current state`);
            entry.resolve();
            continue;
          }

          // Check if we should merge with the last command
          const skipHistory = command.skipHistory === true;

          if (!skipHistory && this.shouldMergeWithLastCommand(command)) {
            console.log(`[CommandManager] Merging with last command`);
            const mergedCommand = this.lastExecutedCommand!.mergeWith!(command);
            this.undoStack[this.undoStack.length - 1] = mergedCommand;
            await mergedCommand.execute();
            this.lastExecutedCommand = mergedCommand;
            this.lastExecutionTime = Date.now();
          } else {
            console.log(`[CommandManager] Executing new command: ${command.name}`);
            await command.execute();
            if (!skipHistory) {
              this.undoStack.push(command);
              this.redoStack = [];
              if (this.undoStack.length > this.options.maxStackSize) {
                this.undoStack.shift();
              }
              this.lastExecutedCommand = command;
              this.lastExecutionTime = Date.now();
            }
          }

          console.log(`[CommandManager] Command executed successfully: ${command.name}`);
          this.eventBus?.emit('command:executed', {
            command,
            canUndo: this.canUndo(),
            canRedo: this.canRedo()
          });
          entry.resolve();
        } catch (error) {
          console.error(`[CommandManager] Error executing command:`, error);
          SessionMetrics.increment('command_errors');
          entry.reject(error);
        }
      }
    } finally {
      this.isExecuting = false;
      console.log(`[CommandManager] Queue processing complete`);
      if (this.commandQueue.length > 0) {
        console.log(`[CommandManager] New commands added during processing, continuing...`);
        void this.processCommandQueue();
      }
    }
  }
  
  async undo(expectedRecoveryId?: string): Promise<void> {
    // Wait for any pending commands to finish
    while (this.isExecuting || this.commandQueue.length > 0) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
    
    const command = this.undoStack[this.undoStack.length - 1];
    if (expectedRecoveryId && command?.historyRecoveryId !== expectedRecoveryId) {
      throw new Error('The command history changed. Return to the pending history action before retrying.');
    }
    if (!command) {
      return;
    }
    this.requireOperationAllowed(command, 'undo');
    this.undoStack.pop();
    
    if (command.canUndo && !command.canUndo()) {
      // Put it back if it can't be undone
      this.undoStack.push(command);
      throw new Error(`Command "${command.name}" cannot be undone`);
    }
    
    this.isExecuting = true;
    
    try {
      await command.undo();
      this.redoStack.push(command);
      
      // Track undo usage (aggregated)
      SessionMetrics.increment('undo_redo');
      
      this.eventBus?.emit('command:undone', {
        command,
        canUndo: this.canUndo(),
        canRedo: this.canRedo()
      });
    } catch (error) {
      // A server-backed undo may be retriable. Never lose the only command
      // object that contains its durable recovery state.
      this.undoStack.push(command);
      throw error;
    } finally {
      this.isExecuting = false;
    }
  }
  
  async redo(expectedRecoveryId?: string): Promise<void> {
    // Wait for any pending commands to finish
    while (this.isExecuting || this.commandQueue.length > 0) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
    
    const command = this.redoStack[this.redoStack.length - 1];
    if (expectedRecoveryId && command?.historyRecoveryId !== expectedRecoveryId) {
      throw new Error('The command history changed. Return to the pending history action before retrying.');
    }
    if (!command) {
      return;
    }
    this.requireOperationAllowed(command, 'redo');
    this.redoStack.pop();
    
    if (command.canRedo && !command.canRedo()) {
      // Put it back if it can't be redone
      this.redoStack.push(command);
      throw new Error(`Command "${command.name}" cannot be redone`);
    }
    
    this.isExecuting = true;
    
    try {
      await command.redo();
      this.undoStack.push(command);
      
      // Track redo usage (aggregated)
      SessionMetrics.increment('undo_redo');
      
      this.eventBus?.emit('command:redone', {
        command,
        canUndo: this.canUndo(),
        canRedo: this.canRedo()
      });
    } catch (error) {
      this.redoStack.push(command);
      throw error;
    } finally {
      this.isExecuting = false;
    }
  }
  
  canUndo(): boolean {
    if (this.undoStack.length === 0) return false;
    const lastCommand = this.undoStack[this.undoStack.length - 1];
    return !lastCommand.canUndo || lastCommand.canUndo();
  }
  
  canRedo(): boolean {
    if (this.redoStack.length === 0) return false;
    const lastCommand = this.redoStack[this.redoStack.length - 1];
    return !lastCommand.canRedo || lastCommand.canRedo();
  }
  
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.lastExecutedCommand = null;
    this.lastExecutionTime = 0;
    
    this.eventBus?.emit('command:cleared', {
      canUndo: false,
      canRedo: false
    });
  }
  
  getUndoStack(): ICommandHistoryEntry[] {
    return this.undoStack.map(cmd => ({
      command: cmd,
      timestamp: cmd.timestamp,
      metadata: cmd.getMetadata?.()
    }));
  }
  
  getRedoStack(): ICommandHistoryEntry[] {
    return this.redoStack.map(cmd => ({
      command: cmd,
      timestamp: cmd.timestamp,
      metadata: cmd.getMetadata?.()
    }));
  }
  
  getHistory(): ICommandHistoryEntry[] {
    return this.getUndoStack();
  }
  
  getLastCommand(): ICommand | null {
    return this.undoStack.length > 0 
      ? this.undoStack[this.undoStack.length - 1] 
      : null;
  }

  patchCreateShapeNodeAttrsInHistory(nodeId: string, attrsPatch: Record<string, unknown>): void {
    for (const cmd of this.undoStack) {
      this.walkCommandsForCreateShapeSnapshotPatch(cmd, nodeId, attrsPatch);
    }
    for (const cmd of this.redoStack) {
      this.walkCommandsForCreateShapeSnapshotPatch(cmd, nodeId, attrsPatch);
    }
  }

  private walkCommandsForCreateShapeSnapshotPatch(
    cmd: ICommand,
    nodeId: string,
    attrsPatch: Record<string, unknown>,
  ): void {
    const maybeScenario = cmd as { getInnerCommand?: () => ICommand };
    if (typeof maybeScenario.getInnerCommand === 'function') {
      this.walkCommandsForCreateShapeSnapshotPatch(maybeScenario.getInnerCommand(), nodeId, attrsPatch);
      return;
    }
    if (cmd instanceof CompositeCommand) {
      for (const inner of cmd.getCommands()) {
        this.walkCommandsForCreateShapeSnapshotPatch(inner, nodeId, attrsPatch);
      }
      return;
    }
    const maybeSync = cmd as {
      syncCreatedNodeAttrsForRedo?: (id: string, patch: Record<string, unknown>) => void;
    };
    if (typeof maybeSync.syncCreatedNodeAttrsForRedo === 'function') {
      maybeSync.syncCreatedNodeAttrsForRedo(nodeId, attrsPatch);
    }
  }
  
  private shouldMergeWithLastCommand(command: ICommand): boolean {
    if (!this.options.mergingEnabled) return false;
    if (command.skipHistory) return false;
    if (!this.lastExecutedCommand) return false;
    if (!command.canMergeWith) return false;
    if (!this.lastExecutedCommand.mergeWith) return false;
    
    // Check time window
    const timeSinceLastCommand = Date.now() - this.lastExecutionTime;
    if (timeSinceLastCommand > this.options.mergingTimeWindow) return false;
    
    // Check if commands can merge
    return command.canMergeWith(this.lastExecutedCommand);
  }
  
  // Batch execute multiple commands as a single undoable operation
  async executeBatch(commands: ICommand[], batchName?: string): Promise<void> {
    if (commands.length === 0) return;
    
    if (commands.length === 1) {
      return this.execute(commands[0]);
    }
    
    const { CompositeCommand } = await import('./ICommand');
    const compositeCommand = new CompositeCommand(
      batchName || 'Batch Operation',
      commands
    );
    
    return this.execute(compositeCommand);
  }
  
  // Transaction support for complex operations
  async transaction<T>(
    name: string,
    operation: (addCommand: (cmd: ICommand) => void) => Promise<T>
  ): Promise<T> {
    const commands: ICommand[] = [];
    
    const addCommand = (cmd: ICommand) => {
      commands.push(cmd);
    };
    
    const result = await operation(addCommand);

    if (commands.length > 0) {
      await this.executeBatch(commands, name);
    }

    return result;
  }
  
  setEventBus(eventBus: IEventBus): void {
    this.eventBus = eventBus;
  }

  /**
   * Install a host-level pre-execution guard. The returned cleanup only clears
   * this exact guard, so a stale React effect cannot remove a newer owner.
   */
  setExecutionGuard(guard: CommandExecutionGuard): () => void {
    this.executionGuard = guard;
    return () => {
      if (this.executionGuard === guard) this.executionGuard = null;
    };
  }

  /** Composable synchronous safety gates apply to all mutation entry points. */
  addOperationGuard(guard: CommandOperationGuard): () => void {
    this.operationGuards.add(guard);
    return () => { this.operationGuards.delete(guard); };
  }
  
  dispose(): void {
    this.clear();
    this.eventBus = null;
    this.executionGuard = null;
    this.operationGuards.clear();
  }
}
